import { motion } from 'framer-motion'
import { useOnboardingStore } from '../store/onboardingStore'
import { V2Icon } from '../components/v2'
import { usesAppLook } from '../lib/variant'

export function SuccessStep() {
  const { data } = useOnboardingStore()
  // S2: the app steps match how the account step signed in, and show the account's
  // own email (a Google account can differ from the one typed earlier). Sessions
  // saved before 2026-10-24 carry neither field: email + password, the typed email.
  const google = data.authMethod === 'google'
  const accountEmail = data.accountEmail || data.email
  const premiumActive = !!data.paddleTransactionId || !!data.webPurchaseClaimed

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md text-center"
        >
          {usesAppLook() ? (
            <V2Icon name="mascot-checkmark" size={96} className="block mx-auto mb-6" />
          ) : (
            <div className="text-6xl mb-6">✅</div>
          )}

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            You're All Set!
          </h1>

          <p className="text-gray-600 mb-8">
            Your account has been created{premiumActive ? ' and your premium plan is active' : ''}.
            Download the KidneyPal app and sign in {google ? 'with Google' : 'with your email'} to get started.
          </p>

          <div className="space-y-4 mb-8">
            <a
              href="https://apps.apple.com/us/app/kidney-pal-renal-diet-tracker/id1523371555"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 w-full py-4 px-6 bg-black text-white rounded-xl hover:bg-gray-800 transition-colors"
            >
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div className="text-left">
                <div className="text-xs">Download on the</div>
                <div className="text-lg font-semibold">App Store</div>
              </div>
            </a>

            <a
              href="https://play.google.com/store/apps/details?id=com.prestigeworldwide.kidneyrecipes"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 w-full py-4 px-6 bg-black text-white rounded-xl hover:bg-gray-800 transition-colors"
            >
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.18 23.04c-.46-.46-.46-1.22-.46-2.75V3.71c0-1.53 0-2.29.46-2.75L13.02 12 3.18 23.04zm1.35.79L16.38 13.2l-2.55-2.55L4.53 23.83zm14.32-10.4l3.28-1.77c.62-.34.62-.98 0-1.32l-3.28-1.77-3.06 2.43 3.06 2.43zM4.53.17l9.3 13.18 2.55-2.55L4.53.17z"/>
              </svg>
              <div className="text-left">
                <div className="text-xs">Get it on</div>
                <div className="text-lg font-semibold">Google Play</div>
              </div>
            </a>
          </div>

          {/* App sign-in mockups (real app screens, 2026-10-13) */}
          <div className="flex gap-4 justify-center mb-8">
            <div className="text-center">
              <p className="text-xs font-medium text-gray-500 mb-2">Step 1: Tap &ldquo;Sign In&rdquo;</p>
              <img
                src="/images/app-signin-step1.png"
                alt="Tap Sign In"
                className="max-w-[200px] w-full rounded-2xl shadow-lg"
              />
            </div>
            <div className="text-center">
              <p className="text-xs font-medium text-gray-500 mb-2">
                Step 2: {google ? 'Continue with Google' : 'Continue with email'}
              </p>
              <img
                src={google ? '/images/app-signin-google.png' : '/images/app-signin-email.png'}
                alt={google ? 'Continue with Google' : 'Continue with email'}
                className="max-w-[200px] w-full rounded-2xl shadow-lg"
              />
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-6 text-left">
            <h3 className="font-semibold text-gray-900 mb-2">Next Steps:</h3>
            <ol className="text-sm text-gray-600 space-y-3">
              <li className="flex gap-2">
                <span className="font-bold text-primary shrink-0">1.</span>
                <span>Download the KidneyPal app from the App Store or Google Play</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-primary shrink-0">2.</span>
                <span>Open the app and tap "Sign In"</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-primary shrink-0">3.</span>
                <span className="break-words">
                  {google ? (
                    <>Tap &ldquo;Continue with Google&rdquo; and choose <strong className="text-gray-900">{accountEmail}</strong></>
                  ) : (
                    <>Tap &ldquo;Continue with email&rdquo; and sign in with <strong className="text-gray-900">{accountEmail}</strong> and the password you created</>
                  )}
                </span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-primary shrink-0">4.</span>
                <span>Your personalized kidney plan is already set up!</span>
              </li>
            </ol>
          </div>

          {premiumActive && (
            <p className="text-sm text-gray-600 mt-6">
              {data.purchasePlanId === '1year' ? "Cancel before your 7-day free trial ends and you won't be charged. " : ''}
              Manage or cancel your subscription anytime at{' '}
              <a href="https://kidneypal.app/account" target="_blank" rel="noopener noreferrer" className="text-primary underline">
                kidneypal.app/account
              </a>.
            </p>
          )}

          <p className="text-xs text-gray-500 mt-6">
            Need help? Contact us at{' '}
            <a href="mailto:support@kidneypal.app" className="text-primary">
              support@kidneypal.app
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  )
}
